/**
 * Source-document review workspace: PDF evidence, OCR recovery, and review forms.
 */
import { formatInteger } from "./format.js";
import {
  detailMetric,
  escapeAttr,
  escapeHtml,
  labelize,
  pill,
  shortUri
} from "./dashboard-html.js";
import { leadDetailSection } from "./dashboard-lead-detail.js";
import {
  extractionJobHtml,
  extractionRouteEvidenceHtml,
  sourceExtractionRouteEvidence
} from "./dashboard-extraction-route.js";
import { taskAssignmentFormHtml } from "./dashboard-task-form.js";
import { eventDetailHtml, factDetailHtml, taskDetailHtml } from "./dashboard-detail-cards.js";
import type {
  LeadFactoryDocumentClassification,
  LeadFactoryOcrResult,
  LeadFactorySourceDocumentReviewResponse,
  LeadFactoryTask,
  SourceDocumentReviewFocus
} from "./dashboard-lead-factory-types.js";

const leadFactoryReviewOutcomes = [
  "classification_confirmed",
  "extraction_confirmed",
  "evidence_confirmed",
  "needs_rework",
  "duplicate_reviewed",
  "not_relevant",
  "invalid_pdf",
  "other_typed_review"
] as const;
const leadFactoryReviewTaskTypes = [
  "classify_document",
  "review_low_confidence_extraction",
  "fetch_referenced_dt",
  "resolve_duplicate",
  "resolve_split",
  "ocr_pdf",
  "enrich_owner"
] as const;

export function sourceDocumentReviewHtml(review: LeadFactorySourceDocumentReviewResponse, focus?: SourceDocumentReviewFocus): string {
  const document = review.document;
  const activeReviewTasks = review.tasks.filter((task) => isActiveReviewTask(task));
  const activeReviewTaskCount = activeReviewTasks.length;
  const focusPage = focus?.page && focus.page > 0 ? focus.page : null;
  const routeEvidence = sourceExtractionRouteEvidence(review);
  return `
    <div class="detail-inner source-document-review">
      <div class="detail-header">
        <div>
          <h2>${escapeHtml(document.id)}</h2>
          <p class="subtle">${escapeHtml(document.county || "unknown county")} · ${escapeHtml(document.property_lead_id || "unlinked source document")}</p>
        </div>
        <div class="status-stack">
          ${pill(labelize(document.source_document_type || "unknown"), document.source_document_type || "unknown")}
          ${pill(labelize(document.classification_status || "unclassified"), document.classification_status || "unclassified")}
          ${pill(labelize(document.extraction_status || "not_started"), document.extraction_status || "not_started")}
        </div>
      </div>

      ${sourceDocumentReviewContextHtml(review)}

      <div class="detail-metrics">
        ${detailMetric("Facts", formatInteger(review.facts.length))}
        ${detailMetric("OCR excerpts", formatInteger(review.ocrResults.length))}
        ${detailMetric("Tasks", formatInteger(review.tasks.length))}
        ${detailMetric("Events", formatInteger(review.events.length))}
      </div>

      <div class="source-document-workspace">
        <section class="source-document-pdf-panel">
          <div class="section-header">
            <h3>PDF Evidence</h3>
            <button class="button secondary" type="button" data-source-document-preview="${escapeAttr(document.id)}">Open PDF</button>
          </div>
          ${
            focusPage
              ? `<div class="manual-fact-review-focus" data-manual-fact-review-focus>
                  <strong>${escapeHtml(focus?.fieldName ? labelize(focus.fieldName) : "Manual fact")} · page ${formatInteger(focusPage)}</strong>
                  <span>${escapeHtml(focus?.evidenceExcerpt || "Review the source page before capturing the fact.")}</span>
                </div>`
              : ""
          }
          <div class="pdf-navigation" data-source-document-pdf-controls="${escapeAttr(document.id)}">
            <button class="button secondary" type="button" data-pdf-page-step="-1" data-source-document-id="${escapeAttr(document.id)}">Prev</button>
            <label>
              <span>Page</span>
              <input type="number" min="1" value="1" inputmode="numeric" data-pdf-page-input="${escapeAttr(document.id)}" />
            </label>
            <button class="button secondary" type="button" data-pdf-page-apply="${escapeAttr(document.id)}">Go</button>
            <button class="button secondary" type="button" data-pdf-page-step="1" data-source-document-id="${escapeAttr(document.id)}">Next</button>
          </div>
          <div class="pdf-frame-shell" data-source-document-inline-preview="${escapeAttr(document.id)}">
            <p class="subtle">Loading protected PDF...</p>
          </div>
          <div class="detail-grid">
            ${detailMetric("Artifact", shortUri(document.artifact_uri || ""))}
            ${detailMetric("SHA", document.sha256 || "unknown")}
            ${detailMetric("Bytes", document.size_bytes ? formatInteger(document.size_bytes) : "unknown")}
            ${detailMetric("Captured", document.captured_at || "unknown")}
          </div>
          <div class="source-review-decision-bar">
            <div>
              <strong>Review Decision</strong>
              <span data-source-review-selected-count>${formatInteger(activeReviewTaskCount)} selected of ${formatInteger(activeReviewTaskCount)} active task${activeReviewTaskCount === 1 ? "" : "s"}</span>
            </div>
            ${sourceReviewDecisionFormHtml(activeReviewTaskCount)}
          </div>
        </section>

        <div class="source-document-evidence-panel">
          ${sourceReviewOcrRetryHtml(review)}
          ${leadDetailSection("Extraction Route", routeEvidence.map(extractionRouteEvidenceHtml), "No extraction route evidence is linked.")}
          ${leadDetailSection("OCR Evidence", review.ocrResults.map((result, index) => ocrResultHtml(result, `ocr-evidence-${index + 1}`)), "No OCR evidence is linked.")}
          ${leadDetailSection("Extracted Facts", review.facts.slice(0, 16).map((fact, index) => factDetailHtml(fact, `fact-evidence-${index + 1}`)), "No extracted facts are linked.")}
          ${leadDetailSection("Tasks", review.tasks.slice(0, 12).map(sourceDocumentTaskDetailHtml), "No source-document tasks are linked.")}
          ${leadDetailSection("Classifications", review.classifications.map(classificationDetailHtml), "No classification history is linked.")}
          ${leadDetailSection("Extraction Jobs", review.extractionJobs.map(extractionJobHtml), "No extraction jobs are linked.")}
          ${leadDetailSection("Workflow Events", review.events.slice(0, 12).map(eventDetailHtml), "No source-document workflow events are linked.")}
        </div>
      </div>
    </div>
  `;
}

export function sourceDocumentReviewContextHtml(review: LeadFactorySourceDocumentReviewResponse): string {
  const document = review.document;
  const activeTasks = review.tasks.filter((task) => ["open", "assigned", "blocked"].includes(task.status));
  const coordinate = [
    document.book && document.page ? `book ${document.book} page ${document.page}` : "",
    document.recording_date || document.document_date || "",
    document.artifact_uri ? shortUri(document.artifact_uri) : "artifact pending"
  ].filter(Boolean).join(" · ");
  return `
    <section class="source-document-review-context" data-source-document-review-context>
      <div>
        <span>Queue context</span>
        <strong>${escapeHtml(document.source_document_type || "unknown source document")}</strong>
        <small>${escapeHtml(coordinate || "No source coordinate loaded")}</small>
      </div>
      <div>
        <span>Evidence loaded</span>
        <strong>${formatInteger(review.facts.length)} facts · ${formatInteger(review.ocrResults.length)} OCR</strong>
        <small>${formatInteger(activeTasks.length)} active tasks · ${formatInteger(review.classifications.length)} classifications</small>
      </div>
      <div class="detail-context-actions">
        <a class="button secondary" href="#lead-production-command-center">Command Center</a>
        ${document.property_lead_id ? `<button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(document.property_lead_id)}">Lead Detail</button>` : ""}
        <a class="button secondary" href="#lead-blockers">Blocker Queue</a>
      </div>
    </section>
  `;
}

export function sourceReviewOcrRetryHtml(review: LeadFactorySourceDocumentReviewResponse): string {
  if (review.ocrResults.length > 0) return "";
  const document = review.document;
  const ocrTasks = review.tasks.filter((task) => task.task_type === "ocr_pdf");
  const completedOcrTaskCount = ocrTasks.filter((task) => task.status === "completed").length;
  const activeOcrTaskCount = ocrTasks.filter((task) => ["open", "assigned", "blocked"].includes(task.status)).length;
  const queuedOcrJobs = review.extractionJobs.filter((job) =>
    job.extractor_version.includes("ocr") || job.status === "queued_for_ocr"
  );
  const retryReason = completedOcrTaskCount > 0
    ? `${formatInteger(completedOcrTaskCount)} completed OCR task${completedOcrTaskCount === 1 ? "" : "s"} without linked evidence`
    : activeOcrTaskCount > 0
      ? `${formatInteger(activeOcrTaskCount)} active OCR task${activeOcrTaskCount === 1 ? "" : "s"} still need evidence`
      : queuedOcrJobs.length > 0
        ? `${formatInteger(queuedOcrJobs.length)} OCR handoff job${queuedOcrJobs.length === 1 ? "" : "s"} waiting on text`
        : "No OCR text is linked to this source document";
  return `
    <section class="source-review-ocr-retry" data-source-review-ocr-retry-panel>
      <div>
        <h3>OCR Recovery</h3>
        <p>${escapeHtml(retryReason)}. Retry only this source document so the operator queue does not reopen unrelated completed OCR work.</p>
      </div>
      <button
        class="button secondary"
        type="button"
        data-source-review-ocr-retry="${escapeAttr(document.id)}"
        data-source-review-ocr-retry-completed="true"
      >Retry OCR for Source</button>
    </section>
  `;
}

export function sourceReviewDecisionFormHtml(activeReviewTaskCount: number): string {
  return `
    <form class="structured-action-form source-review-decision-form" data-source-review-decision-form>
      <select aria-label="Review outcome" data-source-review-outcome ${activeReviewTaskCount === 0 ? "disabled" : ""}>
        ${leadFactoryReviewOutcomes
          .map((outcome) => `<option value="${escapeAttr(outcome)}">${escapeHtml(labelize(outcome))}</option>`)
          .join("")}
      </select>
      <input type="text" maxlength="120" value="evidence_confirmed" aria-label="Review reason" data-source-review-reason ${activeReviewTaskCount === 0 ? "disabled" : ""}>
      <label class="source-review-selector">
        <input type="checkbox" data-source-review-confirm ${activeReviewTaskCount === 0 ? "disabled" : ""}>
        <span>Confirm</span>
      </label>
      <button class="button secondary" type="submit" ${activeReviewTaskCount === 0 ? "disabled" : ""}>Complete selected</button>
    </form>
  `;
}

export function isActiveReviewTask(task: LeadFactoryTask): boolean {
  return ["open", "assigned", "blocked"].includes(task.status) && leadFactoryReviewTaskTypes.includes(task.task_type as (typeof leadFactoryReviewTaskTypes)[number]);
}

export function sourceDocumentTaskDetailHtml(task: LeadFactoryTask): string {
  if (!isActiveReviewTask(task)) return taskDetailHtml(task);
  return `
    <article class="task-item compact source-review-task">
      <div>
        <strong>${escapeHtml(labelize(task.task_type))}</strong>
        <small>${escapeHtml(task.status)} · ${escapeHtml(task.priority)}${task.assigned_to ? ` · ${escapeHtml(task.assigned_to)}` : ""}</small>
        <p>${escapeHtml(task.instructions || task.blocking_reason || "No task instructions.")}</p>
      </div>
      <div class="inline-actions">
        <label class="source-review-selector">
          <input type="checkbox" data-source-review-task-checkbox value="${escapeAttr(task.id)}" checked />
          <span>Select</span>
        </label>
        ${taskAssignmentFormHtml(task)}
        ${pill(labelize(task.status), task.status)}
      </div>
    </article>
  `;
}

export function ocrResultHtml(result: LeadFactoryOcrResult, evidenceId?: string): string {
  return `
    <article class="task-item compact" ${evidenceId ? `id="${escapeAttr(evidenceId)}"` : ""}>
      <div>
        <strong>${escapeHtml(result.ocr_engine || "ocr")}</strong>
        <small>${Math.round((result.confidence ?? 0) * 100)}% confidence · ${escapeHtml(result.created_at)}</small>
        <p>${escapeHtml(result.text_excerpt || result.text_artifact_uri || "No OCR excerpt.")}</p>
      </div>
      ${evidenceId ? `<button class="button secondary" type="button" data-evidence-jump="#${escapeAttr(evidenceId)}">Focus</button>` : ""}
    </article>
  `;
}

export function classificationDetailHtml(classification: LeadFactoryDocumentClassification): string {
  return `
    <article class="task-item compact">
      <div>
        <strong>${escapeHtml(labelize(classification.label))}</strong>
        <small>${Math.round((classification.confidence ?? 0) * 100)}% · ${escapeHtml(classification.review_status || "unreviewed")} · ${escapeHtml(classification.classifier_version)}</small>
        <p>${escapeHtml(classification.evidence || "No classification evidence.")}</p>
      </div>
    </article>
  `;
}
